import React, { useState, useEffect } from "react";
import {
  CustomerFormData,
  OfferingAndSessions,
  Session,
  Student,
} from "../models";

// https://www.carlrippon.com/react-context-with-typescript-p4/

import { createCtx } from "./lib";

export type RegistrationSettings = {};

const DEFAULT_SETTINGS: RegistrationSettings = {};

type RegistrationContextType = {
  settings: RegistrationSettings;
  generateId: () => number;

  offering?: OfferingAndSessions;
  setOffering: (offering?: OfferingAndSessions) => void;

  session?: Session;
  setSession: (session?: Session) => void;

  students: Student[];
  addStudent: (student: Student) => void;
  updateStudent: (student: Student) => void;
  removeStudentWithId: (id: number) => void;

  savedFormData?: CustomerFormData;
  setSavedFormData: (data?: CustomerFormData) => void;
};

export const [useRegistration, CtxProvider] =
  createCtx<RegistrationContextType>();

type Props = {
  children: React.ReactNode;
} & Partial<RegistrationSettings>;

const tempSession: Session = {
  id: 1,
  offering: {
    id: 1,
    course: {
      id: 1,
      displayOrder: 1,
      name: "Course Name",
      description: "Course description",
      currencyCode: "USD",
      amountCents: 120,
      taxCents: 100,
      taxType: "dsasda",
    },
    timezone: "Asia/Dhaka",
    capacity: 1,
  },
  enrolled: 1,
  startsAt: new Date(),
  teachers: [],
};

const tempOffering: OfferingAndSessions = {
  ...tempSession.offering,
  sessions: [tempSession],
  numWeekly: 1,
  schedules: [],
};

export const RegistrationProvider = ({ children, ...userSettings }: Props) => {
  const [offering, setOffering] = useState<OfferingAndSessions>();
  const [session, setSession] = useState<Session>();
  const [students, setStudents] = useState<Student[]>([]);
  const [savedFormData, setSavedFormData] = useState<CustomerFormData>();

  const [nextId, setNextId] = useState(1);

  const settings = {
    ...DEFAULT_SETTINGS,
    ...userSettings,
  };

  const value: RegistrationContextType = {
    settings,
    generateId: () => {
      const id = nextId; // XXX: I'm being paranoid.
      setNextId(id + 1);
      return id;
    },

    savedFormData,
    setSavedFormData,

    offering,
    setOffering,

    session,
    setSession,

    students,
    addStudent: (student: Student) => {
      setStudents([...students, student]);
    },
    updateStudent: (student: Student) => {
      setStudents(
        students.map((s) => {
          if (s.id === student.id) return student;
          return s;
        })
      );
    },
    removeStudentWithId: (id: number) => {
      const index = students.findIndex((s) => s.id === id);
      if (index === -1)
        throw new Error(`Tried to remove non-existent student with id=${id}`);
      setStudents([...students.slice(0, index), ...students.slice(index + 1)]);
    },
  };

  // TODO: useMemo for performance?

  useEffect(() => {
    setSession(tempSession);
    setOffering(tempOffering);
  }, []);

  return <CtxProvider value={value}>{children}</CtxProvider>;
};
